/**
 * Chunked file encryption.
 *
 * Files are encrypted in 4 MiB plaintext chunks rather than as one blob, for
 * three reasons that all matter: a 5 GB single-shot `crypto.subtle.encrypt`
 * would need the whole file in memory twice, R2 multipart uploads want parts
 * anyway, and ranged reads (seeking in a video, rendering page 40 of a PDF)
 * are impossible over one monolithic ciphertext.
 *
 * Chunking a file is where naive implementations get broken, so the two guards
 * below are the point of this module:
 *
 *  **Nonce derivation.** Each chunk's IV is the revision's random 12-byte
 *  `nonceBase` with its low 4 bytes replaced by the chunk index. Reusing a
 *  (key, IV) pair under GCM does not degrade security, it destroys it — it
 *  leaks the XOR of two plaintexts and, worse, the authentication subkey. A
 *  counter in a fixed field makes reuse structurally impossible within a
 *  revision, and a fresh random base per revision makes it impossible across
 *  revisions. This is how TLS constructs its record nonces, for the same reason.
 *
 *  **AAD binding.** Every chunk is authenticated against
 *  `revisionId|index|totalChunks`. GCM alone proves a chunk was not *edited*;
 *  it says nothing about whether it is the chunk that belongs here. Without
 *  this binding a server could silently truncate a file, reorder two chunks, or
 *  splice a chunk from a different file the same user owns, and every
 *  individual decryption would still succeed. With it, all three fail
 *  authentication and the download aborts.
 */

import { randomBytes, type Bytes } from './primitives';

/** Plaintext bytes per chunk. Recorded per revision, so this can change
 *  without stranding files already stored under the old value. */
export const CHUNK_SIZE = 4 * 1024 * 1024;

/** AES-GCM's authentication tag, appended to every chunk's ciphertext. */
export const TAG_BYTES = 16;

export const NONCE_BASE_BYTES = 12;

/**
 * The absurdity bound, and not a product limit.
 *
 * It was 5 GB, matching a "Up to 5 GB a file" the dropzone no longer says — the
 * page has read the caller's real ceiling from `/me` for a while now, because
 * an organisation sets its own and Demo's is 0.25 GB. Leaving the 5 GB here
 * left **two** limits: the page correctly accepted a 20 GB file from a Max
 * customer, who is sold exactly that, and this threw on it a moment later.
 *
 * What is left is a bound on the arithmetic. 1 TiB is past anything any plan
 * sells and short of the 2^32-chunk index wrap below, so a number that reaches
 * this is a bug rather than a big file.
 *
 * The plan's limit lives in `@shared/entitlements`' `maxFileBytesFor`, is
 * enforced by the Worker on `POST /uploads`, and is shown to a person by
 * `Files.tsx`. Three places, each with a different job; this one is not one of
 * them.
 */
export const MAX_FILE_BYTES = 1024 * 1024 * 1024 * 1024;

export const newNonceBase = (): Bytes => randomBytes(NONCE_BASE_BYTES);

export class ChunkIntegrityError extends Error {
  constructor(
    readonly chunkIndex: number,
    message: string
  ) {
    super(message);
  }
}

/**
 * The IV for one chunk: the revision's nonce base with the last four bytes
 * overwritten by a big-endian chunk index.
 *
 * Four bytes caps a revision at 2^32 chunks — 16 petabytes at 4 MiB each, so
 * the index can never wrap in practice. `planUpload` rejects anything near it
 * regardless, because a silent wrap would mean nonce reuse.
 */
export function chunkIv(nonceBase: Uint8Array, index: number): Bytes {
  if (nonceBase.length !== NONCE_BASE_BYTES) {
    throw new Error(`Nonce base must be ${NONCE_BASE_BYTES} bytes.`);
  }
  if (!Number.isInteger(index) || index < 0 || index > 0xffffffff) {
    throw new Error(`Chunk index ${index} is out of range.`);
  }

  const iv = new Uint8Array(nonceBase) as Bytes;
  new DataView(iv.buffer).setUint32(NONCE_BASE_BYTES - 4, index, false);
  return iv;
}

/** What each chunk is authenticated against. Any disagreement between what was
 *  sealed and what is being opened fails the GCM tag. */
export const chunkAad = (revisionId: string, index: number, totalChunks: number): Bytes =>
  new TextEncoder().encode(`${revisionId}|${index}|${totalChunks}`) as Bytes;

export interface UploadPlan {
  chunkCount: number;
  chunkSize: number;
  /** Plaintext size, as reported by the File. */
  plainBytes: number;
  /** What R2 will actually hold, which is what quota is charged on. */
  cipherBytes: number;
}

/**
 * Work out the shape of an upload without encrypting anything.
 *
 * Called before the upload is opened, because the server needs `cipherBytes` to
 * reserve quota and `chunkCount` to record the revision — and both have to be
 * known before the first byte is encrypted, since `chunkCount` is part of every
 * chunk's AAD.
 */
export function planUpload(plainBytes: number, chunkSize = CHUNK_SIZE): UploadPlan {
  if (!Number.isInteger(plainBytes) || plainBytes < 0) {
    throw new Error('File size must be a non-negative integer.');
  }
  if (plainBytes > MAX_FILE_BYTES) {
    throw new Error('That file size is not a real one.');
  }

  // An empty file is one empty chunk, not zero: zero chunks would leave nothing
  // to authenticate, so an empty file would be indistinguishable from a deleted
  // one, and `chunkCount` could not pin the length.
  const chunkCount = plainBytes === 0 ? 1 : Math.ceil(plainBytes / chunkSize);
  if (chunkCount > 0xffffffff) throw new Error('Too many chunks for a 32-bit index.');

  return {
    chunkCount,
    chunkSize,
    plainBytes,
    cipherBytes: plainBytes + chunkCount * TAG_BYTES
  };
}

export interface ChunkContext {
  contentKey: CryptoKey;
  revisionId: string;
  nonceBase: Uint8Array;
  totalChunks: number;
  /** Plaintext bytes per chunk, from the revision. Must be the value the
   *  upload was planned with: deriving it from the file size instead would
   *  disagree with `revisions.chunk_size` and break ranged reads. */
  chunkSize: number;
}

export async function encryptChunk(
  ctx: ChunkContext,
  index: number,
  plaintext: BufferSource
): Promise<Bytes> {
  const ct = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: chunkIv(ctx.nonceBase, index),
      additionalData: chunkAad(ctx.revisionId, index, ctx.totalChunks)
    },
    ctx.contentKey,
    plaintext
  );
  return new Uint8Array(ct) as Bytes;
}

/**
 * Decrypt one chunk, or throw.
 *
 * There is no lenient path here on purpose. A chunk that fails its tag is
 * either corrupt or substituted, and returning anything at all for it — even
 * zeroes, even a shorter file — would hand the caller data the user never
 * wrote.
 */
export async function decryptChunk(
  ctx: ChunkContext,
  index: number,
  ciphertext: BufferSource
): Promise<Bytes> {
  try {
    const plain = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: chunkIv(ctx.nonceBase, index),
        additionalData: chunkAad(ctx.revisionId, index, ctx.totalChunks)
      },
      ctx.contentKey,
      ciphertext
    );
    return new Uint8Array(plain) as Bytes;
  } catch {
    throw new ChunkIntegrityError(
      index,
      `Chunk ${index} failed its integrity check. The file has been altered or is corrupt.`
    );
  }
}

/**
 * Encrypt a file chunk by chunk, yielding each ciphertext as it is produced.
 *
 * A generator rather than a returned array: the caller uploads each chunk and
 * drops it, so peak memory is one plaintext chunk plus one ciphertext chunk
 * regardless of file size. `Blob.slice` does not read from disk until the slice
 * is awaited, so this never holds the whole file either.
 */
export async function* encryptFile(
  file: Blob,
  ctx: ChunkContext,
  onProgress?: (bytesDone: number, total: number) => void
): AsyncGenerator<{ index: number; bytes: Bytes }> {
  const size = file.size;

  for (let index = 0; index < ctx.totalChunks; index++) {
    const start = index * ctx.chunkSize;
    const end = Math.min(start + ctx.chunkSize, size);
    // An empty file still has one chunk, so this slice is legitimately empty.
    const plaintext = new Uint8Array(await file.slice(start, end).arrayBuffer());

    const bytes = await encryptChunk(ctx, index, plaintext);
    plaintext.fill(0);

    onProgress?.(end, size);
    yield { index, bytes };
  }
}

/**
 * A stream of plaintext, decrypted chunk by chunk as the ciphertext arrives.
 *
 * Returned as a `ReadableStream` so a caller can hand it straight to
 * `new Response(stream).blob()`, to a download, or to a `<video>` via a
 * MediaSource — without ever materialising the whole file.
 *
 * `fetchChunk` is injected rather than assumed so the same path serves an
 * authenticated download and a public share link, which authenticate
 * differently but decrypt identically.
 */
export function decryptStream(
  ctx: ChunkContext,
  fetchChunk: (index: number) => Promise<ArrayBuffer>,
  onProgress?: (chunksDone: number, total: number) => void
): ReadableStream<Uint8Array> {
  let index = 0;

  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      if (index >= ctx.totalChunks) {
        controller.close();
        return;
      }

      // Errors propagate to the stream's consumer rather than being swallowed:
      // a truncated download must fail loudly, not produce a short file.
      const ciphertext = await fetchChunk(index);
      controller.enqueue(await decryptChunk(ctx, index, ciphertext));

      index++;
      onProgress?.(index, ctx.totalChunks);
    }
  });
}

/**
 * Decrypt a whole file to a Blob.
 *
 * The convenient path, for thumbnails, previews and anything small enough that
 * streaming buys nothing. Large downloads should use `decryptStream`.
 */
export async function decryptToBlob(
  ctx: ChunkContext,
  fetchChunk: (index: number) => Promise<ArrayBuffer>,
  type = 'application/octet-stream'
): Promise<Blob> {
  const parts: BlobPart[] = [];
  for (let i = 0; i < ctx.totalChunks; i++) {
    parts.push(await decryptChunk(ctx, i, await fetchChunk(i)));
  }
  return new Blob(parts, { type });
}

/**
 * Which chunks a byte range touches, and where the range sits inside them.
 *
 * A chunk is the smallest thing that can be decrypted — the tag covers all of
 * it — so serving bytes 5,000,000–5,000,100 means fetching and opening the
 * chunk containing them and slicing after the fact. Ranged reads are not wired
 * into the UI yet; this is here because the chunk layout is what makes them
 * possible and the arithmetic belongs beside it.
 */
export function chunksForRange(
  start: number,
  end: number,
  chunkSize = CHUNK_SIZE
): { firstChunk: number; lastChunk: number; offsetInFirst: number } {
  if (start < 0 || end < start) throw new Error('Invalid byte range.');
  return {
    firstChunk: Math.floor(start / chunkSize),
    lastChunk: Math.floor(end / chunkSize),
    offsetInFirst: start % chunkSize
  };
}
